/**
 * StyleHub组件 - 产品图片展示组件
 * 基于原Vue组件迁移，支持PC端轮播和移动端网格布局
 */

'use client';

import HomePageTitle from '@/app/(home-page)/components/HomePageTitle';
import { useFetchDynamicProducts } from '@/app/(home-page)/hooks/useFetchDynamicProducts';
import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { getAlt } from '@/utils/tools';
import { RightArrowOutlined } from '@zeelool/icon';
import Image from '@/components/Image';
import { useTranslations } from 'next-intl';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperCore } from 'swiper/types';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import SkeletonProductCard from '@/components/Product/SkeletonProductCard';
interface ProductInfo {
  image_url: string;
  short_name: string;
  price_info?: {
    sell_price: number;
  };
}

interface ProductItem {
  id: string | number;
  product_id: string | number;
  shop_sku: string;
  business_sku?: string;
  sku: string;
  short_name: string;
  jump_image: string;
  jump_img_ratio?: string;
  product_info: ProductInfo;
}

interface IndexData {
  blocks_advances_id: string;
  id: string | number;
  component_id: string;
  layout_title?: string;
  layout_title_status?: number;
  layout_title_color?: string;
  layout_subtitle?: string;
  layout_subtitle_color?: string;
  btn_text?: string;
  btn_bg_color?: string;
  btn_word_color?: string;
  product_group_id?: string;
  bg_color?: string;
  products_num?: number;
  products?: ProductItem[];
  // 跳转相关属性
  jump_type?: number;
  Jump_target?: string;
  product_group?: {
    id: string;
    path?: string;
  };
}

// 埋点参数接口
interface TrackingParams {
  module_index: number;
  module_name: string;
  mkt_index: number;
  mkt_index_sub: string;
  mkt_jump_type: string;
  mkt_jump_content: string;
  module_tab_name: string;
  coupon_id_list: string;
  coupon_name_list: string;
  shop_sku: string;
  business_sku: string;
  product_sku: string;
  product_id: string;
  product_name: string;
  group_id: string;
  group_name: string;
  module_title: string;
  module_id: string;
}

interface StyleHubProps {
  indexData: IndexData;
  moduleIndex: number;
  onTrack?: (params: TrackingParams) => void;
  onExpTrack?: (params: TrackingParams) => void;
  onValidExpTrack?: (params: TrackingParams) => void;
}

// 埋点逻辑Hook
const useTrackingHandlers = (
  trackingParams: TrackingParams,
  onTrack?: (params: TrackingParams) => void,
  onExpTrack?: (params: TrackingParams) => void,
  onValidExpTrack?: (params: TrackingParams) => void,
) => {
  // 曝光埋点
  const [expRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 处理曝光埋点
  useEffect(() => {
    if (inView && onExpTrack) {
      try {
        onExpTrack(trackingParams);
      } catch (error) {
        logger.error('StyleHub exposure tracking error:', error);
      }
    }
  }, [inView, onExpTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });

  // 处理点击埋点
  const handleClick = useCallback(() => {
    if (onTrack) {
      try {
        onTrack(trackingParams);
      } catch (error) {
        logger.error('StyleHub click tracking error:', error);
      }
    }
  }, [onTrack, trackingParams]);

  return { expRef, handleClick, validExposureRef };
};

// 获取埋点参数
const useTrackingParams = (
  item: ProductItem,
  index: number,
  moduleIndex: number,
  componentId: string,
  moduleTitle: string,
  moduleId: string,
): TrackingParams => {
  return useMemo(
    () => ({
      module_index: moduleIndex,
      module_name: componentId,
      mkt_index: index,
      mkt_index_sub: '',
      mkt_jump_type: '2',
      mkt_jump_content: `${item.product_id}`,
      module_tab_name: '',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: `${item.shop_sku}`,
      business_sku: `${item.business_sku || ''}`,
      product_sku: `${item.sku}`,
      product_id: `${item.product_id}`,
      product_name: `${item.short_name}`,
      group_id: '',
      group_name: '',
      module_title: moduleTitle || '',
      module_id: String(moduleId || ''),
    }),
    [item, index, moduleIndex, componentId, moduleTitle],
  );
};

/**
 * 产品项组件 - PC端展示
 */
const ProductItemPC = ({
  item,
  index,
  indexData,
  onTrack,
  onExpTrack,
  onValidExpTrack,
  moduleIndex,
}: {
  item: ProductItem;
  index: number;
  indexData: IndexData;
  onTrack?: (params: TrackingParams) => void;
  onExpTrack?: (params: TrackingParams) => void;
  onValidExpTrack?: (params: TrackingParams) => void;
  moduleIndex: number;
}) => {
  const globalT = useTranslations('');

  const [isHovered, setIsHovered] = useState(false);

  // 获取当前货币信息和用户信息
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  // 获取埋点参数
  const trackingParams = useTrackingParams(
    item,
    index,
    moduleIndex,
    indexData.component_id,
    indexData.layout_title || '',
    indexData.blocks_advances_id || '',
  );

  // 获取埋点处理函数
  const { expRef, handleClick, validExposureRef } = useTrackingHandlers(
    trackingParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  );

  // 获取价格信息
  const productPriceInfo = useMemo(() => {
    return priceInfo(userInfo, item.product_info, currency);
  }, [userInfo, item.product_info, currency]);

  const expMergedRef = useMergedRefs(expRef, validExposureRef);

  return (
    <div ref={expMergedRef} className="relative w-[inherit]">
      <MagicJump
        prefetch
        item={{
          jump_type: 2,
          Jump_target: item.shop_sku,
          sku: { shop_sku: item.shop_sku },
        }}
        onBeforeJump={handleClick}
        aria-label={item.short_name}
        className="block"
      >
        <div
          className="relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Image
            src={item.jump_image}
            alt={getAlt('StyleHub', index, indexData)}
            width={438}
            height={597}
            loading={moduleIndex < 3 ? 'eager' : 'lazy'}
            className="w-full h-full object-cover"
            style={{
              aspectRatio: item.jump_img_ratio || '0.7336',
            }}
          />

          {/* 悬停信息层 */}
          {isHovered && (
            <div className="absolute inset-0 bg-black/60 py-[40px] px-[15px] lg:py-[48px] lg:px-[20px] 2xl:py-[58px] 2xl:px-[24px] box-border">
              <div className="bg-[#EEEEEE] w-full h-full flex flex-col items-center justify-center p-[20px] lg:p-[24px] pt-[30px] lg:pt-[40px] 2xl:pt-[70px] min-h-[200px] min-w-[150px]">
                {/* 产品图片 */}
                <Image
                  src={item.product_info.image_url}
                  alt={item.product_info.short_name}
                  width={315}
                  height={158}
                  className="w-[150px] lg:w-[250px] 2xl:w-[315px] h-auto object-contain mix-blend-multiply flex-shrink-0"
                  style={{ aspectRatio: '2/1' }}
                />

                {/* 产品信息 */}
                <div className="mt-[16px] lg:mt-[30px] 2xl:mt-[60px] 3xl:mt-[90px] text-center flex-grow flex flex-col flex-start">
                  <p className="text-[11px] lg:text-[13px] 2xl:text-[14px] text-[#282828] leading-tight mb-[30px] lg:mb-[20px] 2xl:mb-[25px]">
                    <span className="font-br block lg:inline">{item.product_info.short_name}</span>
                    {productPriceInfo?.sell_price && (
                      <span className="ml-0 lg:ml-[8px] font-bb block lg:inline mt-[4px] lg:mt-0">
                        {formatPriceNumberWithSymbol(productPriceInfo.sell_price, currency)}
                      </span>
                    )}
                  </p>

                  {/* Shop Now 按钮 */}
                  <div className="w-[120px] lg:w-[140px] 2xl:w-[189px] h-[30px] lg:h-[35px] 2xl:h-[48px] mx-auto flex items-center justify-center rounded-[36px] border border-black text-[12px] lg:text-[13px] 2xl:text-[16px] text-[#282828] font-br">
                    {globalT('account.memberLevel.shopNow')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </MagicJump>
    </div>
  );
};

/**
 * 产品项组件 - 移动端展示
 */
const ProductItemMobile = ({
  item,
  index,
  indexData,
  onTrack,
  onExpTrack,
  moduleIndex,
  onValidExpTrack,
}: {
  item: ProductItem;
  index: number;
  indexData: IndexData;
  onTrack?: (params: TrackingParams) => void;
  onExpTrack?: (params: TrackingParams) => void;
  onValidExpTrack?: (params: TrackingParams) => void;
  moduleIndex: number;
}) => {
  // 获取埋点参数
  const trackingParams = useTrackingParams(
    item,
    index,
    moduleIndex,
    indexData.component_id,
    indexData.layout_title || '',
    indexData.blocks_advances_id || '',
  );

  // 获取埋点处理函数
  const { expRef, handleClick, validExposureRef } = useTrackingHandlers(
    trackingParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  );

  const expMergedRef = useMergedRefs(expRef, validExposureRef);

  return (
    <div ref={expMergedRef}>
      <MagicJump
        prefetch
        item={{
          jump_type: 2,
          Jump_target: item.shop_sku,
          sku: { shop_sku: item.shop_sku },
        }}
        onBeforeJump={handleClick}
        aria-label={item.short_name}
        className="block"
      >
        <Image
          src={item.jump_image}
          alt={getAlt('StyleHub', index, indexData)}
          width={200}
          height={223}
          loading="eager"
          className="w-full h-[223px] object-cover"
          style={{
            aspectRatio: item.jump_img_ratio || 'auto',
          }}
        />
      </MagicJump>
    </div>
  );
};

import { usePageCache } from '@/hooks/usePageCache';

/**
 * StyleHub主组件
 */
export default function StyleHub({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: StyleHubProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;
  const swiperRef = useRef<SwiperCore>(null);

  // 为每个组件实例生成唯一ID
  const uniqueId = useMemo(
    () => `style-hub-${String(indexData.id || moduleIndex).replace(/[^a-zA-Z0-9-]/g, '-')}`,
    [indexData.id, moduleIndex],
  );

  // 页面缓存：用于返回首页时保持数据
  const { pageState, updatePageState } = usePageCache({
    pageKey: 'zl-homepage-dynamic-products',
    initialData: {},
  });

  const cachedData = pageState[uniqueId];
  const [dynamicProductsData, setDynamicProductsData] = useState<any>(cachedData?.data || null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(
    cachedData?.hasAttemptedFetch || false,
  );

  // 当全局缓存更新时，同步到本地状态（用于处理路由返回时页面恢复）
  useEffect(() => {
    const currentCache = pageState[uniqueId];
    if (currentCache && currentCache.hasAttemptedFetch && !hasAttemptedFetch) {
      setDynamicProductsData(currentCache.data);
      setHasAttemptedFetch(currentCache.hasAttemptedFetch);
    }
  }, [pageState, uniqueId, hasAttemptedFetch]);

  // 监听数据变化，同步到缓存
  const cachedDataRef = useRef(pageState[uniqueId] || {});
  useEffect(() => {
    // 只有当数据真正发生变化，并且不是来自初始缓存时才更新，防止死循环
    if (dynamicProductsData === null && hasAttemptedFetch === false) return; // 初始状态不缓存

    const hasDataChanged = dynamicProductsData !== cachedDataRef.current.data;
    const hasAttemptedFetchChanged = hasAttemptedFetch !== cachedDataRef.current.hasAttemptedFetch;

    if (hasDataChanged || hasAttemptedFetchChanged) {
      cachedDataRef.current = {
        data: dynamicProductsData,
        hasAttemptedFetch,
      };
      updatePageState({
        [uniqueId]: cachedDataRef.current,
      });
    }
  }, [dynamicProductsData, hasAttemptedFetch, uniqueId, updatePageState]);

  // 产品列表
  const navList = useMemo(() => {
    const dataSource = { ...indexData, ...dynamicProductsData };
    return dataSource?.products || [];
  }, [indexData?.products, dynamicProductsData]);

  const hasProducts = navList && navList.length > 0;

  const { ref: inViewRef, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  const currency = useStore((state) => state.currency);

  const fetchDynamicProducts = useFetchDynamicProducts({
    componentId: indexData?.component_id,
    componentBlockId: indexData?.id,
    currencyName: currency?.name,
    dynamicProductsData,
    hasAttemptedFetch,
    isLoading,
    setDynamicProductsData,
    setIsLoading,
    setHasAttemptedFetch,
  });

  useEffect(() => {
    // 确保有必要的参数再发请求，且未尝试过获取数据
    if (inView && !hasProducts && !isLoading && !hasAttemptedFetch) {
      fetchDynamicProducts();
    }
  }, [fetchDynamicProducts, hasAttemptedFetch, hasProducts, inView, isLoading]);

  // Swiper配置
  const swiperOptions = useMemo(
    () => ({
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      slidesPerView: 'auto' as const,
      spaceBetween: 17,
      allowTouchMove: true,
      breakpoints: {
        1024: {
          slidesPerView: 4 as const,
          spaceBetween: 16,
          allowTouchMove: false,
        },
      },
      onSwiper: (swiper: SwiperCore) => {
        swiperRef.current = swiper;
      },
    }),
    [],
  );

  // 如果请求过数据并且数据为空，则不渲染该组件
  if (hasAttemptedFetch && !hasProducts) {
    return null;
  }

  // 如果产品数量不足4个，不渲染
  if (indexData.products_num < 4) {
    return null;
  }

  return (
    <div ref={inViewRef} className={'w-full'}>
      {isPC ? (
        // PC端轮播展示
        <div className="relative">
          {/* 标题部分 */}
          <div
            className={`flex items-center justify-between mb-[24px] lg:mb-[32px] ${isPC ? 'mx-[24px] lg:mx-[48px]' : ''}`}
          >
            <HomePageTitle
              indexData={indexData}
              variant="inline"
              displayCondition="pc-only"
              titleTag="h3"
              subtitleTag="h5"
              hasButton={!!indexData.btn_text}
            />

            {/* 按钮部分 */}
            {indexData.btn_text && (
              <div className="flex-shrink-0">
                <MagicJump
                  prefetch
                  item={{
                    jump_type: indexData.jump_type || 0,
                    Jump_target: indexData.Jump_target || '',
                    product_group: {
                      id: indexData.product_group?.id as string,
                      path: indexData.product_group?.path,
                    },
                  }}
                  className="inline-block"
                >
                  <div
                    className="flex items-center justify-center min-w-[113px] lg:min-w-[7.2916666667vw] h-[36px] lg:h-[48px] rounded-[25px] px-[12px] lg:px-[15px] text-[12px] lg:text-[16px] cursor-pointer border border-transparent"
                    style={{
                      color: indexData.btn_word_color || '#ffffff',
                      backgroundColor: indexData.btn_bg_color || '#282828',
                      borderColor: indexData.btn_word_color || '#ffffff',
                    }}
                  >
                    <span>{indexData.btn_text}</span>
                    <RightArrowOutlined className="ml-[5px] text-[12px]" />
                  </div>
                </MagicJump>
              </div>
            )}
          </div>

          {/* 轮播容器 */}
          <div className="">
            <Swiper
              {...swiperOptions}
              className="!pl-[24px] lg:!pl-[48px] !pr-[24px] lg:!pr-[48px]"
            >
              {hasProducts
                ? navList.map((item, index) => (
                    <SwiperSlide
                      key={item.id || index}
                      className="min-w-[234px] max-w-[234px] lg:!min-w-[fit-content] lg:!max-w-[fit-content]"
                    >
                      <ProductItemPC
                        item={item}
                        index={index}
                        indexData={indexData}
                        onTrack={onTrack}
                        onExpTrack={onExpTrack}
                        onValidExpTrack={onValidExpTrack}
                        moduleIndex={moduleIndex}
                      />
                    </SwiperSlide>
                  ))
                : Array.from({ length: indexData.products_num }).map((_, index) => (
                    <SwiperSlide
                      key={`skeleton-${index}`}
                      className="min-w-[234px] max-w-[234px] lg:!min-w-[fit-content] lg:!max-w-[fit-content]"
                    >
                      <div className="w-[234px] lg:w-[234px] aspect-[438/597]">
                        <SkeletonProductCard />
                      </div>
                    </SwiperSlide>
                  ))}
            </Swiper>
          </div>
        </div>
      ) : (
        // 移动端网格展示
        <div className="my-[40px]">
          {/* 标题部分 */}
          <HomePageTitle
            indexData={indexData}
            variant="default"
            displayCondition="mobile-only"
            titleTag="h3"
            subtitleTag="h5"
            isNew
            hasButton={false}
          />

          {/* 产品网格 */}
          <ul className="grid grid-cols-2 mt-[20px]">
            {hasProducts
              ? navList.map((item, index) => (
                  <li key={item.id || index}>
                    <ProductItemMobile
                      item={item}
                      index={index}
                      indexData={indexData}
                      onTrack={onTrack}
                      onExpTrack={onExpTrack}
                      onValidExpTrack={onValidExpTrack}
                      moduleIndex={moduleIndex}
                    />
                  </li>
                ))
              : Array.from({ length: indexData.products_num }).map((_, index) => (
                  <li
                    key={`skeleton-${index}`}
                    className="w-full h-[223px] flex items-center justify-center"
                  >
                    <div className="w-[60%] h-[223px] flex items-center justify-center">
                      <SkeletonProductCard />
                    </div>
                  </li>
                ))}
          </ul>

          {/* 查看更多按钮 */}
          {indexData.btn_text && (
            <div className="flex justify-center mt-[30px]">
              <MagicJump
                prefetch
                item={{
                  jump_type: indexData.jump_type || '',
                  Jump_target: indexData.Jump_target || '',
                  product_group: {
                    id: indexData.product_group?.id as string,
                    path: indexData.product_group?.path,
                  },
                }}
                className="inline-block"
              >
                <div
                  className="min-w-[190px] h-[40px] leading-[40px] text-center rounded-[20px] font-bb text-[14px] cursor-pointer transition-all duration-200 hover:opacity-90"
                  style={{
                    color: indexData.btn_word_color || '#ffffff',
                    backgroundColor: indexData.btn_bg_color || '#282828',
                  }}
                >
                  {indexData.btn_text}
                </div>
              </MagicJump>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
